import React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@spoiler-alert/ui-library';

type StepperColorTheme = 'teal' | 'green';

const themeColors: Record<StepperColorTheme, { base: string; light: string }> = {
  teal: { base: Theme.teal, light: Theme.teal10 },
  green: { base: Theme.primaryColor, light: Theme.green10 },
};

interface StepCardProps {
  icon: React.ReactNode;
  label: string;
  index: number;
  colorTheme: StepperColorTheme;
  isActive: boolean;
  onClick: () => void;
}

const useStepStyles = createUseStyles({
  card: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    borderRadius: 6,
    cursor: 'pointer',
    position: 'relative',
    transition: 'box-shadow 0.15s',
    background: ({ light }: { base: string; light: string }) => `linear-gradient(105deg, ${light} 7.25%, transparent 86.38%)`,
    '&:hover': {
      boxShadow: ({ base }: { base: string; light: string }) => `inset 0 0 0 1px ${base}`,
    },
  },
  cardActive: {
    boxShadow: ({ base }: { base: string; light: string }) => `inset 0 0 0 1px ${base}`,
  },
  arrow: {
    position: 'absolute',
    right: -12,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 0,
    height: 0,
    borderTop: '10px solid transparent',
    borderBottom: '10px solid transparent',
    borderLeft: ({ base }: { base: string; light: string }) => `12px solid ${base}`,
  },
  icon: {
    fontSize: 28,
    marginRight: 14,
    flexShrink: 0,
    lineHeight: 1,
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
  },
  stepNumber: {
    fontSize: 14,
    fontWeight: 800,
    lineHeight: '16px',
    textTransform: 'uppercase',
    color: ({ base }: { base: string; light: string }) => base,
  },
  label: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '-0.176px',
    color: Theme.textColorPrimary,
  },
});

export function StepCard({ icon, label, index, colorTheme, isActive, onClick }: StepCardProps) {
  const colors = themeColors[colorTheme];
  const classes = useStepStyles(colors);

  return (
    <div className={`${classes.card} ${isActive ? classes.cardActive : ''}`} onClick={onClick}>
      {icon && <div className={classes.icon}>{icon}</div>}
      <div className={classes.text}>
        <span className={classes.stepNumber}>Step {index}</span>
        <span className={classes.label}>{label}</span>
      </div>
      {isActive && <div className={classes.arrow} />}
    </div>
  );
}
